import { useState, type ReactNode } from 'react';
import { cn } from '@/shared/lib';
import { styles } from './styles';
import type { DetailSection, DetailTab } from './types';

/**
 * Tab strip with roving-tabindex state.
 *
 * The active tab is layout state, not module state — a module should not have
 * to think about which panel is open. Roving tabindex is implemented explicitly
 * because it is the accessibility contract: exactly one tab is in the tab
 * sequence, arrow keys move between them, and each panel is associated with its
 * tab via aria-controls / aria-labelledby.
 */
export function TabbedSections({
  sections,
  tabs,
}: {
  sections: ReadonlyArray<DetailSection>;
  tabs: ReadonlyArray<DetailTab>;
}): ReactNode {
  const [activeId, setActiveId] = useState(tabs[0]?.id ?? '');

  /**
   * Wraps around at both ends. Arrow-key navigation that stops at the boundary
   * makes the last item feel broken; the roving pattern wraps by convention.
   */
  const move = (delta: number): void => {
    const index = tabs.findIndex((tab) => tab.id === activeId);
    const next = (index + delta + tabs.length) % tabs.length;
    const target = tabs[next];
    if (target) setActiveId(target.id);
  };

  return (
    <div className={styles.tabbed}>
      {sections
        .filter((section) => section.inline)
        .map((section) => (
          <BodySectionCard key={section.id} section={section} />
        ))}

      <div role="tablist" aria-label="Record sections" className={styles.tabList}>
        {tabs.map((tab) => (
          <BodyTabButton
            key={tab.id}
            tab={tab}
            selected={tab.id === activeId}
            onSelect={() => {
              setActiveId(tab.id);
            }}
            onMove={move}
          />
        ))}
      </div>

      {sections
        .filter((section) => !section.inline)
        .map((section) => (
          <BodyTabPanel key={section.id} section={section} selected={section.id === activeId} />
        ))}
    </div>
  );
}

/** One body tab button. Roving tabindex: only the selected tab is in the tab order. */
function BodyTabButton({
  tab,
  selected,
  onSelect,
  onMove,
}: {
  tab: DetailTab;
  selected: boolean;
  onSelect: () => void;
  onMove: (delta: number) => void;
}): ReactNode {
  return (
    <button
      type="button"
      role="tab"
      id={`${tab.id}-tab`}
      aria-controls={`${tab.id}-panel`}
      aria-selected={selected}
      tabIndex={selected ? 0 : -1}
      className={cn(styles.tab, selected ? styles.tabActive : styles.tabInactive)}
      onClick={onSelect}
      onKeyDown={(event) => {
        if (event.key === 'ArrowRight') {
          event.preventDefault();
          onMove(1);
        }
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          onMove(-1);
        }
      }}
    >
      {tab.icon ? (
        <span aria-hidden="true" className={styles.tabIcon}>
          {tab.icon}
        </span>
      ) : null}
      <span>{tab.label}</span>
      {typeof tab.count === 'number' ? (
        <span className={cn(styles.tabCount, selected ? styles.tabCountActive : styles.tabCountInactive)}>
          {tab.count}
        </span>
      ) : null}
    </button>
  );
}

/** Body variant of the section card. Local to this file so `sections.tsx` stays under 200 lines. */
function BodySectionCard({ section }: { section: DetailSection }): ReactNode {
  return (
    <article className={styles.section} aria-labelledby={`${section.id}-heading`}>
      <h2 id={`${section.id}-heading`} className={styles.sectionHeading}>
        {section.label}
      </h2>
      {section.content}
    </article>
  );
}

/** Body variant of the tab panel. Local to this file. */
function BodyTabPanel({ section, selected }: { section: DetailSection; selected: boolean }): ReactNode {
  return (
    <div
      role="tabpanel"
      id={`${section.id}-panel`}
      aria-labelledby={`${section.id}-tab`}
      className={styles.panel}
      hidden={!selected}
    >
      <h2 className={styles.sectionHeading}>{section.label}</h2>
      {section.content}
    </div>
  );
}